import { Box, Text, Input, Button, VStack } from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';
import FileUploadCard from 'components/FileUploadCard';
import type { APIApplicationFile } from 'types/api.types';

interface SupportingFilesSectionProps {
  uploadedFiles: APIApplicationFile[];
  onFilesUpload?: (files: File[]) => Promise<void>;
  onFileRemove?: (fileId: number) => void;
  isUploadingFile?: boolean;
  isUploading?: boolean;
  isDeletingFileId?: number | null;
  readOnly?: boolean;
}

const SupportingFilesSection = ({
  uploadedFiles,
  onFilesUpload,
  onFileRemove,
  isUploadingFile,
  isUploading,
  isDeletingFileId = null,
  readOnly = false,
}: SupportingFilesSectionProps) => {
  const uploading = isUploadingFile ?? isUploading ?? false;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onFilesUpload) {
      const fileArray = Array.from(files);
      await onFilesUpload(fileArray);
    }
    // Reset input
    e.target.value = '';
  };

  return (
    <Box>
      <Text fontWeight="bold" fontSize="md" mb={2}>
        File Upload
      </Text>
      <Text fontSize="sm" color="gray.600" mb={3}>
        Upload supporting documentation (e.g., PDFs, images, or spreadsheets) to
        strengthen your case.
      </Text>

      {/* File Upload Area — hidden in readOnly mode */}
      {!readOnly && (
        <Box mb={3}>
          <Input
            id="file-upload"
            type="file"
            multiple
            display="none"
            onChange={handleFileChange}
            accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.doc,.docx"
            disabled={uploading}
          />
          <label htmlFor="file-upload">
            <Button
              as="span"
              leftIcon={<AttachmentIcon />}
              colorScheme="blue"
              variant="outline"
              size="md"
              isLoading={uploading}
              loadingText="Uploading..."
              cursor="pointer"
              width="full"
              maxW="300px"
            >
              Choose Files
            </Button>
          </label>
          <Text fontSize="xs" color="gray.500" mt={2}>
            Supported: PDF, Images, Excel, Word
          </Text>
        </Box>
      )}

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <VStack align="stretch" spacing={3}>
          {uploadedFiles.map((file) => (
            <FileUploadCard
              key={file.id}
              file={file}
              badgeText="Supporting Document"
              badgeColorScheme="blue"
              onDelete={onFileRemove ?? (() => {})}
              isDeleting={isDeletingFileId === file.id}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default SupportingFilesSection;
